"use client";

import React from "react";

import { useState, useEffect, useRef } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";

interface VisaModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: any; 
  code: string;
  type: string;
  onAccept: () => void;
  onDecline: (reason: string) => void;
}


interface visaDetailsType {
  address1: string;
  code: string;
  email: string;
  full_name: string;
  id: number;
  name: string;
  title?: string;
  surname?: string;
  given_name?: string;
  nationality?: string;
  place_of_birth?: string;
  passport_country: string;
  passport_number: string;
  passport_issue_date?: string;
  passport_expiry_date?: string;
  registration_id: string;
  rejection_reason: string;
  birth_date?: string;
  gender?: string;
  status: string;
  type: string;
}

const DECLINE_REASONS = [
  "Document is not legible",
  "Document is expired",
  "Document is not official",
  "Information doesn't match student records",
  "Document appears to be altered",
  "Other",
];

export default function VisaModal({
  isOpen,
  onClose,
  row,
  code,
  type,
  onAccept,
  onDecline,
}: VisaModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [textReason, setTextReason] = useState<string>("");
  const [isDeclineActive, setIsDeclineActive] = useState<boolean>(false);
  const [visaDetails, setVisaDetails] = useState<visaDetailsType | null>(null); // Adjust the type as needed

  const modalRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside the modal to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    }

    // Handle escape key to close modal
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      // Prevent scrolling when modal is open
      document.body.style.overflow = "hidden";
      const fetchVisaDetails = async () => {
        try {
          const response = await axios.get(
            `${base_url}/api/admin/get_visa_details/${code}/${type}`);
          setVisaDetails(response.data);
        } catch (error) {
          console.error("Error fetching visa details:", error);
        }
      };

      fetchVisaDetails();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    console.log("Modal is open:", isOpen);
  }, []);

  const handleAccept = () => {
    onAccept();
    onClose();
  };

  const handleDecline = () => {
    const fun = async () => {
      setIsDeclineActive(false);
      try {
        await axios.post(`${base_url}/api/admin/decline_visa_letter`, {
          reason: textReason,
          student_id: row.student_id,
          code: row.code,
          type: row.type,
        });

        onDecline(textReason);
        onClose();
      } catch (error) {
        console.error("Error declining student proof:", error);
      }
    }
    fun();
  };

  const resetForm = () => {
    setSelectedReason("");
    setTextReason("");
    setIsDeclineActive(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleReasonSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedReason(value);
    // Prefill the text field with the selected reason
    if (value !== "Other") {
      setTextReason(value);
    } else {
      setTextReason("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl  flex flex-col p-6 relative"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Header */}
        <div className="mb-4">
          <h2 id="modal-title" className="text-xl font-bold">
            Visa letter review - requested data ({visaDetails?.type})
          </h2>
        </div>

        <div className="flex-1 min-h-0 mb-4 p-4 border border-gray-300 rounded-md overflow-hidden">

          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Code:</span>
            <span className="text-gray-900">{visaDetails?.code}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Title:</span>
            <span className="text-gray-900">{visaDetails?.title}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Surname:</span>
            <span className="text-gray-900">{visaDetails?.surname || visaDetails?.full_name}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Given Name:</span>
            <span className="text-gray-900">{visaDetails?.given_name}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Sex:</span>
            <span className="text-gray-900">{visaDetails?.gender}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Nationality:</span>
            <span className="text-gray-900">{visaDetails?.nationality}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Date of birth:</span>
            <span className="text-gray-900">{visaDetails?.birth_date}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Place of birth:</span>
            <span className="text-gray-900">{visaDetails?.place_of_birth}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Passport No.:</span>
            <span className="text-gray-900">{visaDetails?.passport_number}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Date of issue:</span>
            <span className="text-gray-900">{visaDetails?.passport_issue_date}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-700 font-bold w-1/4">Date of expiry:</span>
            <span className="text-gray-900">{visaDetails?.passport_expiry_date}</span>
          </div>
        </div>

        {/* Actions */}
        {isDeclineActive ? (
          <div className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="decline-reason"
                  className="block text-sm font-medium text-gray-700"
                >
                  Reason for declining
                </label>
                <select
                  id="decline-reason"
                  value={selectedReason}
                  onChange={handleReasonSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>
                    Select a reason
                  </option>
                  {DECLINE_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="text-reason"
                  className="block text-sm font-medium text-gray-700"
                >
                  {selectedReason === "Other"
                    ? "Custom reason"
                    : "Edit reason (if needed)"}
                </label>
                <textarea
                  id="text-reason"
                  placeholder={
                    selectedReason === "Other"
                      ? "Please specify the reason for declining"
                      : "Edit the reason if needed"
                  }
                  value={textReason}
                  onChange={(e) => setTextReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                />
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handleDecline()}
                disabled={!selectedReason || !textReason.trim()}
                className={`px-4 py-2 rounded-md text-white ${!selectedReason || !textReason.trim()
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                  }`}
              >
                Confirm Decline
              </button>
              <button
                onClick={() => setIsDeclineActive(false)}
                className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between">
            <button
              onClick={() => setIsDeclineActive(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            >
              <XCircle className="h-4 w-4" />
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4" />
              Accept
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
